import { ZonedDateTime } from '@js-joda/core';
import { Box, Typography } from '@mui/material';
import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  DateField,
  DateTimeInput,
  DeleteWithConfirmButton,
  Edit,
  Filter,
  FunctionField,
  ReferenceField,
  required,
  SaveButton,
  Show,
  ShowButton,
  SimpleForm,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  AdminCohortDetailsFragment,
  AdminEligibleEmailDetailsFragment,
  EligibleEmailAddress,
} from '../../graphql/types';
import useCanWrite from '../../hooks/useCanWrite';
import { iso8601DateTime } from '../../utils/date';
import { EnvLevel, getEnvLevel } from '../../utils/getEnvLevel';

import { IDSearch } from '../reusable/IDSearch';
import { ResourceLink } from '../reusable/ResourceLink';
import AutocompleteClientInput from './components/AutocompleteClientInput';
import AutocompleteCohortInput from './components/AutocompleteCohortInput';
import AutocompleteOfficeInput from './components/AutocompleteOfficeInput';
import {
  EligibilityWindow,
  EligibilityWindowView,
  parseZone,
  ZoneColumn,
} from './components/EligibilityWindow';
import InfiniteList from './components/InfiniteList';
import {
  endAfter,
  formatUtcDateTimeInput,
  parseUtcDateTimeInput,
  parseZonedDateTime,
} from './components/utcDateTimeInput';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

const EligibleEmailAddressToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const EligibleEmailAddressFilters: React.FC = (props) => (
  <Filter {...props}>
    <IDSearch label="Email" source="email" alwaysOn />
    <TextInput label="Last Name" source="lastName" alwaysOn />
    <TextInput label="Employee ID" source="employeeId" alwaysOn />
    <BooleanInput label="Has HPC Eligibility" source="hasHpcEligibility" />
    <AutocompleteCohortInput source="cohortId" label="Cohort" alwaysOn />
    <AutocompleteClientInput source="clientId" label="Client" alwaysOn />
    <AutocompleteOfficeInput source="officeId" label="Office" alwaysOn />
  </Filter>
);

// The list query already fetches `user` via AdminEligibleEmailDetails, so this
// renders from the record instead of a ReferenceField — no extra manyUsers call.
const EligibleEmailUserField: React.FC<{ label?: string }> = () => {
  const record = useRecordContext<AdminEligibleEmailDetailsFragment>();
  const user = record?.user;

  if (!user) {
    return <>Not registered</>;
  }

  return (
    <ResourceLink name="User" id={user.id} type="show">
      {user.email}
    </ResourceLink>
  );
};

export const EligibleEmailAddressList: React.FC = () => (
  <InfiniteList filters={<EligibleEmailAddressFilters />}>
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <TextField source="email" sortable={false} />
      <TextField source="lastName" sortable={false} />
      <TextField source="source" sortable={false} />
      <BooleanField source="isArchived" label="Archived" sortable={false} />
      <BooleanField source="isStaff" label="Staff" sortable={false} />
      <BooleanField
        source="hasHpcEligibility"
        label="Has HPC Eligibility"
        sortable={false}
      />
      <ReferenceField
        source="cohort.id"
        reference="Cohort"
        label="Cohort"
        link="show"
        emptyText="—"
        sortable={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <EligibleEmailUserField label="User" />
      <ReferenceField
        source="client.id"
        reference="Client"
        label="Client"
        sortable={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <WriteOnlyEditButton />
      <ShowButton />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

const EligibleEmailAddressShowActions: React.FC = () => {
  const canWrite = useCanWrite();
  const record = useRecordContext<AdminEligibleEmailDetailsFragment>();

  const canDelete = !record?.user && record?.addedManually;

  return (
    <TopToolbar>
      <WriteOnlyEditButton />
      {record && canDelete && canWrite && (
        <DeleteWithConfirmButton
          confirmColor="warning"
          confirmTitle="Delete Eligible Email Address"
          confirmContent="Are you sure you want to delete this eligible email address?"
        />
      )}
    </TopToolbar>
  );
};

export const EligibleEmailAddressShow: React.FC = () => (
  <Show actions={<EligibleEmailAddressShowActions />}>
    <TabbedShowLayout>
      <Tab label="Basics">
        <TextField source="id" />
        <TextField source="email" />
        <TextField source="source" label="Eligibility Source" />
        <BooleanField source="isStaff" label="Staff" />
        <BooleanField source="user.registered" label="User Registered" />
        <ReferenceField source="user.id" reference="User" label="User">
          <FunctionField
            render={(record: Record<string, any>) =>
              record &&
              `${record.firstName} ${record.lastName} <${record.email}>`
            }
          />
        </ReferenceField>
        <BooleanField source="isArchived" label="Archived" />
        <FunctionField
          label="FCM Eligible"
          render={(record: EligibleEmailAddress) => {
            if (record.office) {
              return record.office.isFcmEligible
                ? 'Yes, via office'
                : 'No, via office';
            } else {
              return record.isFcmEligible ? 'Yes' : 'No';
            }
          }}
        />
        <FunctionField
          label="App Eligible"
          render={(record: EligibleEmailAddress) => {
            if (record.office) {
              return record.office.isAppEligible
                ? 'Yes, via office'
                : 'No, via office';
            } else {
              return record.isAppEligible ? 'Yes' : 'No';
            }
          }}
        />
        <DateField source="archivedAt" />
        <ReferenceField source="client.id" reference="Client" label="Client">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="office.id" reference="Office" label="Office">
          <TextField source="clientOfficeId" />
        </ReferenceField>
        <ReferenceField
          source="user.id"
          reference="User"
          label="User"
          link="show"
          emptyText="Not registered"
        />
      </Tab>
      <Tab label="High-Performance Coaching">
        <BooleanField
          source="hasHpcEligibility"
          label="Eligible Email Has HPC Eligibility"
          sortable={false}
        />
        <BooleanField
          source="user.hasHighPerformanceCoaching"
          label="User HPC Eligibility Is Active"
          sortable={false}
        />
        <ReferenceField
          source="cohort.id"
          reference="Cohort"
          label="Cohort"
          link="show"
          emptyText="None, dates set manually"
        >
          <TextField source="name" />
        </ReferenceField>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Eligibility Window
        </Typography>
        <EligibilityWindow
          startSource="hpcEligibilityStart"
          endSource="hpcEligibilityEnd"
          timezoneSource="user.timezone"
        />
      </Tab>
      <Tab label="Other">
        <TextField source="invitingRelationship" />
        <ReferenceField
          source="invitingUser.id"
          reference="User"
          label="Inviting User"
        >
          <TextField source="email" />
        </ReferenceField>
        <TextField source="employeeId" />
        <TextField source="employeeType" />
        <TextField source="lastName" />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

const StaffInput: React.FC = () => (
  <BooleanInput
    source="isStaff"
    defaultValue
    helperText="If this is unchecked, the user can be archived the next time an eligibility file is uploaded"
  />
);

const useMemberZone = (): ZoneColumn | null => {
  const record = useRecordContext<AdminEligibleEmailDetailsFragment>();
  const memberTz = record?.user?.timezone ?? null;
  const memberZone = memberTz ? parseZone(memberTz) : null;
  return memberZone
    ? { header: `Member (${memberTz})`, zone: memberZone }
    : null;
};

const HpcWindowPreview: React.FC<{
  start: ZonedDateTime | null;
  end: ZonedDateTime | null;
}> = ({ start, end }) => {
  const userZone = useMemberZone();
  return (
    <Box sx={{ mb: 2 }}>
      <EligibilityWindowView start={start} end={end} userZone={userZone} />
      {!userZone && (
        <Typography variant="caption" color="text.disabled">
          Member timezone unknown
        </Typography>
      )}
    </Box>
  );
};

const HpcManualDateInputs: React.FC = () => {
  // Live preview of the window the inputs describe, updating as the admin edits.
  const start = parseZonedDateTime(useWatch({ name: 'hpcEligibilityStart' }));
  const end = parseZonedDateTime(useWatch({ name: 'hpcEligibilityEnd' }));

  return (
    <>
      <Typography style={{ marginBottom: 16 }}>
        User-level access to high-performance coaching is based on the specified
        start and end dates.
      </Typography>
      <DateTimeInput
        source="hpcEligibilityStart"
        label="Start Date (UTC)"
        format={formatUtcDateTimeInput}
        parse={parseUtcDateTimeInput}
        required
      />
      <DateTimeInput
        source="hpcEligibilityEnd"
        label="End Date (UTC)"
        format={formatUtcDateTimeInput}
        parse={parseUtcDateTimeInput}
        validate={[required(), endAfter('hpcEligibilityStart')]}
      />
      <HpcWindowPreview start={start} end={end} />
    </>
  );
};

export const HpcInputs: React.FC = (props) => {
  const record = useRecordContext(props);
  const [showDates, setShowDates] = React.useState(record?.hasHpcEligibility);
  const cohortId = useWatch({ name: 'cohortId' });
  const { getValues, setValue } = useFormContext();
  // The selected cohort's dates override manual ones, so show them read-only.
  const { data: cohort } = useGetOne<AdminCohortDetailsFragment>(
    'Cohort',
    { id: cohortId },
    { enabled: !!cohortId },
  );

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDates(event.target.checked);
    // Default the dates here rather than with input defaultValues: the manual
    // date preview's useWatch doesn't see input-level defaults, so it would
    // stay empty.
    if (event.target.checked && !getValues('hpcEligibilityStart')) {
      setValue(
        'hpcEligibilityStart',
        iso8601DateTime(ZonedDateTime.now().withFixedOffsetZone()),
      );
    }
    if (event.target.checked && !getValues('hpcEligibilityEnd')) {
      setValue(
        'hpcEligibilityEnd',
        iso8601DateTime(
          ZonedDateTime.now().plusMonths(3).withFixedOffsetZone(),
        ),
      );
    }
  };

  return (
    <>
      <BooleanInput
        source="hasHpcEligibility"
        label="Has High-Performance Coaching Eligibility"
        onChange={handleToggle}
        helperText="Do not turn OFF after cohort ends. Use the End Date instead. Turning off removes dates and breaks reporting."
      />
      {showDates && (
        <>
          <AutocompleteCohortInput
            source="cohortId"
            label="Cohort"
            helperText="Members of a cohort get its start and end dates. Leave empty to set dates manually."
          />
          {cohortId ? (
            <>
              <Typography style={{ marginBottom: 16 }}>
                User-level access to high-performance coaching is based on the
                cohort's start and end dates.
              </Typography>
              <HpcWindowPreview
                start={parseZonedDateTime(cohort?.startDate)}
                end={parseZonedDateTime(cohort?.endDate)}
              />
            </>
          ) : (
            <HpcManualDateInputs />
          )}
        </>
      )}
    </>
  );
};

export const ArchivedAtInput: React.FC = () => {
  const isArchived = useWatch({ name: 'isArchived' });

  if (getEnvLevel() !== EnvLevel.DEV) {
    return null;
  }

  if (!isArchived) {
    return null;
  }

  return (
    <DateTimeInput
      source="archivedAt"
      label="Archived At (UTC)"
      format={formatUtcDateTimeInput}
      parse={parseUtcDateTimeInput}
      helperText="Dev only. Leave empty to use the current time."
    />
  );
};

const AutocompleteOfficeInputWithRecord: React.FC<{
  source: string;
  label: string;
  isRequired?: boolean;
}> = ({ source, label, isRequired }) => {
  const record = useRecordContext<AdminEligibleEmailDetailsFragment>();

  return (
    <AutocompleteOfficeInput
      source={source}
      label={label}
      clientId={record?.client?.id}
      isRequired={isRequired}
      disabled={record?.addedManually !== true} // Only allow editing if added manually
    />
  );
};

const EligibilityInputs: React.FC = () => {
  const manualEligibility = useWatch({ name: 'manualEligibility' });
  return (
    <div style={{ marginTop: 16 }}>
      <BooleanInput
        source="manualEligibility"
        label="Manually Edit App/FCM Eligibility"
      />
      {manualEligibility && (
        <BooleanInput source="isAppEligible" label="Set App Eligibility" />
      )}
      {manualEligibility && (
        <BooleanInput source="isFcmEligible" label="Set FCM Eligibility" />
      )}
    </div>
  );
};

export const EligibleEmailAddressEdit: React.FC = () => (
  <Edit mutationMode="pessimistic" redirect={false}>
    <SimpleForm toolbar={<EligibleEmailAddressToolbar />}>
      <TextInput source="id" InputProps={{ disabled: true }} />
      <TextInput source="email" InputProps={{ disabled: true }} />
      <TextInput
        source="source"
        InputProps={{ disabled: true }}
        helperText="Eligibility sources include file, mbo, admin, or invitation."
      />
      <div style={{ marginTop: 16 }}>
        <AutocompleteOfficeInputWithRecord
          source="office.id"
          label="Office"
          isRequired={false}
        />
      </div>
      <BooleanInput source="isArchived" />
      <ArchivedAtInput />
      <StaffInput />
      <HpcInputs />
      <EligibilityInputs />
    </SimpleForm>
  </Edit>
);

const OfficeInputWithClientFilter: React.FC = () => {
  const clientId = useWatch({ name: 'client.id' });
  const { setValue } = useFormContext();
  const prevClientIdRef = React.useRef(clientId);

  React.useEffect(() => {
    // Clear office when client changes
    if (prevClientIdRef.current !== clientId) {
      setValue('officeId', null);
      prevClientIdRef.current = clientId;
    }
  }, [clientId, setValue]);

  return (
    <AutocompleteOfficeInput
      source="officeId"
      label="Office"
      clientId={clientId}
      isRequired={false}
    />
  );
};

export const EligibleEmailAddressCreate: React.FC = () => (
  <Create>
    <SimpleForm>
      <TextInput source="email" />
      <AutocompleteClientInput source="client.id" label="Client" />
      <BooleanInput source="isArchived" />
      <OfficeInputWithClientFilter />
      <StaffInput />
      <HpcInputs />
      <EligibilityInputs />
    </SimpleForm>
  </Create>
);
